import React, { useEffect, useState, useContext } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Text from '../../components/Text';
import Link from '../../components/Link';
import EmailPromptModal from '../../components/EmailPromptModal';
import { URLSNAV } from '../../constants/urls';
import { API_URLS } from '../../../config';
import { HeaderContext } from '../../../HeaderContext';
import { PreviewContext } from '../../../PreviewContext';
import UserContext from "../../../UserContext";
import './styles/LeaveFeedbackPage.css';

const EMAIL_PROMPT_MIN_SECONDS = 8; // Minimum seconds before showing email prompt for positive reviews
const EMAIL_PROMPT_KEY_PREFIX = 'emailPrompted-';

const LeaveFeedbackPage = () => {
  const { setHeaderAnimated, setHeaderExtended } = useContext(HeaderContext);
  const { isPreview, previewLinks, customLinks } = useContext(PreviewContext);
  const { token: jwtToken, bulkData } = useContext(UserContext);
  const location = useLocation();
  const [links, setLinks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const linksReference = React.useRef([]);
  const leaveHeaderReferene = React.useRef();
  const { id } = useParams();

  // Track when the user completed a positive review (store timestamp in sessionStorage)
  useEffect(() => {
    const inPreviewMode = isPreview || location.search.includes('preview=true');
    if (!inPreviewMode && bulkData && bulkData[id]?.isSent && bulkData[id]?.generalRating >= 4) {
      const key = EMAIL_PROMPT_KEY_PREFIX + id;
      if (!sessionStorage.getItem(key)) {
        sessionStorage.setItem(key, Date.now().toString());
      }
    }
  }, [bulkData, id, isPreview, location.search]);

  // Show email prompt if user returns after minimum seconds for positive reviews
  useEffect(() => {
    const inPreviewMode = isPreview || location.search.includes('preview=true');
    if (!inPreviewMode) {
      const key = EMAIL_PROMPT_KEY_PREFIX + id;
      const lastPrompt = sessionStorage.getItem(key);
      const emailGiven = sessionStorage.getItem(key + '-done');
      if (lastPrompt && !emailGiven && bulkData && bulkData[id]?.generalRating >= 4) {
        const elapsed = (Date.now() - parseInt(lastPrompt, 10)) / 1000;
        if (elapsed >= EMAIL_PROMPT_MIN_SECONDS) {
          setShowEmailPrompt(true);
        } else {
          // Set a timer to show after the remaining time
          const timeout = setTimeout(() => setShowEmailPrompt(true), (EMAIL_PROMPT_MIN_SECONDS - elapsed) * 1000);
          return () => clearTimeout(timeout);
        }
      }
    }
  }, [id, isPreview, location.search, bulkData]);

  useEffect(() => {
    setHeaderAnimated(true);
    setHeaderExtended(false);
    
    setTimeout(() => {
      setShow(true);
    }, 50);

    // If custom links are provided for preview, use those
    if (customLinks) {
      setLinks(customLinks);
      setIsLoading(false);
      return;
    }

    // If in preview mode, use the default preview links
    if (isPreview) {
      console.log("LeaveFeedbackPage: Using preview links");
      setLinks(previewLinks);
      setIsLoading(false);
      return;
    }

    // If not in preview mode, fetch links from API
    fetch(API_URLS.referrals(id), {
      headers: {
        'Authorization': `Bearer ${jwtToken}`
      }
    })
    .then(response => response.json())
    .then(data => {
      console.log("Fetched links:", data);
      const fetchedLinks = data.map(link => ({
        id: link.id,
        url: link.link_url,
        src: link.provider_image,
        name: link.provider_name,
        show: false
      }));
      setLinks(fetchedLinks);
      setIsLoading(false);
    })
    .catch(error => {
      console.error("Error fetching links:", error);
      setIsLoading(false);
    });
  }, [id, jwtToken, isPreview, previewLinks, customLinks, setHeaderAnimated, setHeaderExtended]);

  // Links Animation
  useEffect(() => {
    const showLink = (index) => {
      if (index >= links.length) return;

      setLinks(prevLinks => prevLinks.map((link, idx) => {
        if (idx === index) {
          return {
            ...link,
            show: true
          };
        }
        return link;
      }));

      setTimeout(() => showLink(index + 1), 200); // adjust delay as needed
    };

    if (links.length > 0) {
      setTimeout(() => showLink(0), 400); // Start after a delay
    }
  }, [links]);

  return (
    <>
      <CSSTransition
        timeout={400}
        classNames="fade-in-up"
        nodeRef={leaveHeaderReferene}
        in={show}
        unmountOnExit
      >
        <div ref={leaveHeaderReferene} className="leave-feedback-header">
          <Text className="leave-feedback-title" content="We are glad you had a great experience!" />
          <Text className="leave-feedback-text" content="We would appreciate it if you could leave a positive review on one of these platforms." />
        </div>
      </CSSTransition>
      {isLoading ? (
        <div className="loading">
          <Text className="loading-text" content="Loading..." />
        </div>
      ) : (
        <div className='leave-feedback-container'>
          {links.map((link, index) => (
            <CSSTransition
              key={link.id}
              in={link.show}
              timeout={400}
              classNames="fade-in-up"
              nodeRef={linksReference.current[index]}
              unmountOnExit
            >
              <div className="cosas" ref={el => linksReference.current[index] = el}>
                <Link src={link.src} url={link.url} alt={link.name} />
              </div>
            </CSSTransition>
          ))}
        </div>
      )}
      <EmailPromptModal
        open={showEmailPrompt}
        onClose={() => setShowEmailPrompt(false)}
        onSubmit={email => {
          // Here you would send the email to your backend if desired
          console.log('Email submitted for positive review:', email);
          sessionStorage.setItem(EMAIL_PROMPT_KEY_PREFIX + id + '-done', 'true');
          setShowEmailPrompt(false);
        }}
      />
    </>
  );
};

export default LeaveFeedbackPage;
